// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
import type {
  ExportDocumentSummary,
  ExportFiles,
  ExportJobSettings,
  ExportRunResult,
  ExportSettingsSaveRequest,
  FilingPlan,
} from './desktop-api';

/**
 * How long a new job keeps its backup copies (owner decision, 2026-10-04): 30 days, not
 * Forever, whose folder only grows until the disk is full. Only a job with no saved export
 * settings starts here; a saved choice, Forever included, is never changed, because
 * shortening it would delete backup copies the job already has.
 */
export const NEW_JOB_BACKUP_DAYS = 30 as const;

export type ExportSettingsDraft = {
  destinationKind: 'filesystem' | 'content-central';
  destinationBindingId?: string | null;
  destinationChanged: boolean;
  triggerMode: ExportJobSettings['triggerMode'];
  pdfProfile: ExportJobSettings['pdfProfile'];
  retentionDays: ExportJobSettings['retentionDays'];
  filingPlan: FilingPlan;
  scheduleLocalTime: string;
  scheduleTimeZone: string;
  /** A folder export's files (ADR-0096); absent, the comparison leaves them out. A saved job without one has the default. */
  files?: ExportFiles;
  /** Another exceptions folder was chosen and not saved yet. */
  exceptionsChanged?: boolean;
};

/**
 * A manual dispatch always runs the engine's durable settings, never renderer
 * state. Keep the comparison in one pure function so every visible edit which
 * has not reached that durable record holds the action.
 */
export const exportSettingsNeedSave = (
  saved: ExportJobSettings | null,
  draft: ExportSettingsDraft,
): boolean => {
  if (!saved) return true;
  const savedDestination = saved.destinationKind ?? 'filesystem';
  if (
    savedDestination !== draft.destinationKind ||
    draft.destinationChanged ||
    draft.exceptionsChanged
  )
    return true;
  if (draft.files !== undefined && (saved.files ?? 'pdf-data-text') !== draft.files) return true;
  if (
    saved.triggerMode !== draft.triggerMode ||
    saved.pdfProfile !== draft.pdfProfile ||
    saved.retentionDays !== draft.retentionDays ||
    saved.enabled !== true
  )
    return true;
  if (JSON.stringify(saved.filingPlan) !== JSON.stringify(draft.filingPlan)) return true;
  if (
    draft.destinationKind === 'content-central' &&
    (saved.destinationBindingId ?? null) !== (draft.destinationBindingId ?? null)
  )
    return true;
  if (
    draft.triggerMode === 'scheduled' &&
    ((saved.scheduleLocalTime ?? '') !== draft.scheduleLocalTime ||
      (saved.scheduleTimeZone ?? '') !== draft.scheduleTimeZone)
  )
    return true;
  return false;
};

/**
 * Whether saved export settings have somewhere to send files: a folder, or a Content Central
 * binding. The engine's export preview counts only a saved, enabled export with one of these.
 */
export const exportHasDestination = (
  settings:
    | Pick<ExportJobSettings, 'destinationKind' | 'destinationRoot' | 'destinationBindingId'>
    | null
    | undefined,
): boolean =>
  !settings
    ? false
    : (settings.destinationKind ?? 'filesystem') === 'content-central'
      ? Boolean(settings.destinationBindingId)
      : Boolean(settings.destinationRoot?.trim());

/**
 * Whether export is on after a save of Export's draft (owner decision, 2026-10-26, replacing
 * owner decision 2 of 2026-09-25 and the 2026-10-04 rule that only Start exporting turned it on).
 * The switch says on or off itself (`turnOn`). Any other save keeps the switch as it was, except
 * the save that gives a job its first destination: that one turns export on, from whichever
 * panel or from Job setup's Save. "First" is read against the settings the screen loaded: a job
 * whose saved settings had no destination has never been on. A later save never changes the
 * switch, so a job someone turned off stays off when its naming is edited and saved.
 */
export const exportEnabledAfterSave = ({
  wasOn,
  firstDestination,
  turnOn,
}: {
  /** The saved settings are on. */
  wasOn: boolean;
  /** The settings loaded for this job had no destination, so this save gives it its first. */
  firstDestination: boolean;
  /** The switch's own save: on or off as asked. */
  turnOn?: boolean;
}): boolean => turnOn ?? (wasOn || firstDestination);

/**
 * The switch's own save: the saved settings exactly as they are, with only on or off changed.
 * No folder token, so the coordinator keeps the saved folder and backup folder; a draft of other
 * choices on screen stays a draft for Save.
 */
export const exportSwitchRequest = (
  saved: ExportJobSettings,
  enabled: boolean,
): ExportSettingsSaveRequest => {
  const destinationKind = saved.destinationKind ?? 'filesystem';
  return {
    jobId: saved.jobId,
    triggerMode: saved.triggerMode,
    pdfProfile: saved.pdfProfile,
    retentionDays: saved.retentionDays,
    filingPlan: saved.filingPlan,
    scheduleLocalTime: saved.triggerMode === 'scheduled' ? (saved.scheduleLocalTime ?? null) : null,
    scheduleTimeZone: saved.triggerMode === 'scheduled' ? (saved.scheduleTimeZone ?? null) : null,
    enabled,
    destinationKind,
    destinationBindingId:
      destinationKind === 'content-central' ? (saved.destinationBindingId ?? null) : null,
  };
};

/** When documents leave, in the words beside the switch: "as soon as they’re ready", "daily at 5:00 PM", "only when I say". */
export const exportWhenWords = (
  trigger: ExportJobSettings['triggerMode'],
  dailyAt: string,
): string =>
  trigger === 'continuous'
    ? 'as soon as they’re ready'
    : trigger === 'scheduled'
      ? `daily at ${dailyAt}`
      : 'only when I say';

/**
 * The line under the switch: what on or off means for this job's timing. On Autopilot the daily time is the
 * destination's alone: what Paige can't file leaves for the exceptions folder as soon as it is ready (owner,
 * 2026-10-31). Off and "only when I say" hold the exceptions with everything else.
 */
export const exportSwitchStatus = (
  on: boolean,
  trigger: ExportJobSettings['triggerMode'],
  dailyAt: string,
  autopilot = false,
): string =>
  !on
    ? 'Nothing leaves this job.'
    : trigger === 'manual'
      ? 'Nothing is sent until you click Export now.'
      : trigger === 'scheduled'
        ? `Ready documents leave together daily at ${dailyAt}.${autopilot ? ' Documents Paige can’t file go to the exceptions folder as soon as they’re ready.' : ''}`
        : 'Documents leave as soon as they are ready.';

/** A run's counts, with the documents it froze when the engine sent them. */
type ExportRunCounts = Pick<ExportRunResult, 'exported' | 'failed' | 'held'> & {
  documents?: ExportRunResult['documents'];
};

/**
 * A run's failed documents, split by whether they reached their folder or Content
 * Central. One that arrived and only missed its backup copy is not a delivery that
 * failed: it waits in Export activity as "Delivered · backup copy pending".
 */
export const exportRunFailures = (
  result: ExportRunCounts,
): { undelivered: number; backupPending: number } => {
  const delivered = (result.documents ?? []).filter(
    (document) =>
      (document.state === 'failed' || document.state === 'uncertain') &&
      document.remoteEffectState === 'verified' &&
      document.localRecoveryState !== 'verified',
  ).length;
  const backupPending = Math.min(result.failed, delivered);
  return { undelivered: result.failed - backupPending, backupPending };
};

/**
 * A run's documents sent to the exceptions folder (ADR-0096). The engine counts them in `exported`, but they did not
 * go to the job's destination, so they are counted apart.
 */
export const exportRunExceptions = (result: ExportRunCounts): number =>
  Math.min(
    result.exported,
    (result.documents ?? []).filter(
      (document) => document.route === 'exceptions' && document.state === 'exported',
    ).length,
  );

/** One honest summary for every combination a manual dispatch can return. */
export const exportRunNotice = (result: ExportRunCounts): string => {
  const { undelivered, backupPending } = exportRunFailures(result);
  const exceptions = exportRunExceptions(result),
    exported = result.exported - exceptions;
  const parts = [
    exported > 0 ? `${exported} ${exported === 1 ? 'document' : 'documents'} exported` : null,
    exceptions > 0
      ? `${exceptions} ${exceptions === 1 ? 'document' : 'documents'} sent to the exceptions folder`
      : null,
    backupPending > 0
      ? `${backupPending} ${backupPending === 1 ? 'document' : 'documents'} delivered, backup copy pending`
      : null,
    undelivered > 0
      ? `${undelivered} ${undelivered === 1 ? 'document needs' : 'documents need'} attention`
      : null,
    result.held > 0
      ? `${result.held} ${result.held === 1 ? 'document is' : 'documents are'} not ready yet`
      : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' · ') : 'Nothing is waiting to export';
};

/** What telemetry sends for that summary instead of its words: documents that were not delivered, else nothing (a plain toast). */
export const exportRunNoticeCode = (
  result: ExportRunCounts,
): 'export-documents-failed' | undefined =>
  exportRunFailures(result).undelivered > 0 ? 'export-documents-failed' : undefined;

/** Where each non-delivered document actually went after a manual dispatch. */
export const exportRunAttentionDescription = (result: ExportRunCounts): string => {
  const { undelivered, backupPending } = exportRunFailures(result);
  // A document sent to the exceptions folder is not a delivery (ADR-0096): it is said apart.
  const exceptions = exportRunExceptions(result);
  const delivered = result.exported - exceptions + backupPending;
  const parts = [
    delivered > 0
      ? `${delivered} ${delivered === 1 ? 'document was' : 'documents were'} delivered.`
      : null,
    exceptions > 0
      ? `${exceptions} ${exceptions === 1 ? 'document was' : 'documents were'} sent to the exceptions folder.`
      : null,
    result.held > 0
      ? `${result.held} ${result.held === 1 ? 'remains' : 'remain'} in Documents for review.`
      : null,
    undelivered > 0
      ? `${undelivered} ${undelivered === 1 ? 'document' : 'documents'} moved to Export activity for recovery.`
      : null,
  ].filter((part): part is string => part !== null);
  return parts.join(' ');
};

/**
 * "backup copy until <date>" for an Activity row whose backup copy is still kept, else null.
 * The engine keeps the date on a cleaned-up (purged) export as the record of when the copy
 * was due, so the date alone does not mean there is still a copy.
 */
export const exportHistoryBackupUntil = (
  document: Pick<ExportDocumentSummary, 'state' | 'purgeEligibleAtUtc'>,
): string | null =>
  document.state !== 'purged' && document.purgeEligibleAtUtc
    ? `backup copy until ${new Date(document.purgeEligibleAtUtc).toLocaleDateString()}`
    : null;

/** Keep durable engine states out of the customer-facing history vocabulary. */
export const exportHistoryStateLabel = (state: string, verifiedAtLabel?: string | null): string => {
  if (state === 'purged') return 'Backup copy cleaned up';
  if (state === 'failed') return 'Export needs attention';
  if (state === 'uncertain') return 'Checking the destination';
  if (state === 'canceled') return 'Returned to work';
  if (state === 'building' || state === 'delivering' || state === 'frozen')
    return 'Export in progress';
  return verifiedAtLabel || 'Exported';
};

/**
 * The cleanup line under an Activity row, or none. A queued cleanup of the backup copy is the
 * ordinary state of every export kept for a while, and the row already says "backup copy until
 * <date>", so it gets no line (every settled export used to carry "cleanup is queued", which
 * read as a problem). That holds only while the date is still ahead: once it has passed, or with
 * no date, the row no longer explains why the copy is still there, so the line comes back.
 * Every other cleanup keeps its line: one that is running, waiting to retry or needs someone,
 * and the short-lived temporary cleanup after an export or a return to work, which the row
 * shows nothing else about.
 */
export const exportHistoryCleanupNote = (
  document: Pick<
    ExportDocumentSummary,
    | 'maintenanceState'
    | 'maintenanceKind'
    | 'maintenanceMessage'
    | 'maintenanceNextAttemptAtUtc'
    | 'purgeEligibleAtUtc'
  >,
  now: Date = new Date(),
): { state: NonNullable<ExportDocumentSummary['maintenanceState']>; text: string } | null => {
  if (!document.maintenanceMessage) return null;
  const state = document.maintenanceState ?? 'queued';
  const keptUntil = document.purgeEligibleAtUtc
    ? Date.parse(document.purgeEligibleAtUtc)
    : Number.NaN;
  if (
    state === 'queued' &&
    document.maintenanceKind === 'retention-purge' &&
    keptUntil > now.getTime()
  )
    return null;
  const nextAttempt =
    state === 'held' && document.maintenanceNextAttemptAtUtc
      ? ` Next attempt ${new Date(document.maintenanceNextAttemptAtUtc).toLocaleString()}.`
      : '';
  return { state, text: `${document.maintenanceMessage}${nextAttempt}` };
};

/**
 * The history filter is a local calendar day, not a 24-hour duration. Building
 * the following local midnight calendrically keeps spring-forward and fall-back
 * days exact before the engine receives the half-open UTC range.
 */
export const exportHistoryDateRange = (localDate: string): { from: string; to: string } | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(localDate);
  if (!match) return null;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const start = new Date(year, monthIndex, day);
  if (start.getFullYear() !== year || start.getMonth() !== monthIndex || start.getDate() !== day)
    return null;
  const end = new Date(year, monthIndex, day + 1);
  return { from: start.toISOString(), to: end.toISOString() };
};

/** The one place Export's state is shown: a dot and a few words. */
export type ExportStatusKind = 'loading' | 'not-set-up' | 'unsaved' | 'ready' | 'attention';

/** Picks the one status to show; attention outranks everything, then unsaved, then set-up. */
export function exportStatus({
  loading,
  enabled,
  unsaved,
  needsAttention,
  attentionText,
  lastExportAt,
  exportedToday,
}: {
  loading: boolean;
  enabled: boolean;
  unsaved: boolean;
  needsAttention: number;
  attentionText?: string | null;
  lastExportAt: string | null;
  exportedToday: number | null;
}): { kind: ExportStatusKind; text: string } {
  if (loading) return { kind: 'loading', text: 'Checking…' };
  if (needsAttention > 0)
    return {
      kind: 'attention',
      text: `${needsAttention.toLocaleString()} ${needsAttention === 1 ? 'document needs' : 'documents need'} attention`,
    };
  if (attentionText) return { kind: 'attention', text: attentionText };
  // On or off is the switch's to say, beside this (owner decision, 2026-10-26); while export is
  // off the status says only what Activity holds. ("Not set up" over a filled-in folder read as a
  // contradiction, owner 2026-09-28, and "Not exporting yet" repeated the switch.)
  if (!enabled)
    return {
      kind: 'not-set-up',
      text: exportedToday
        ? `${exportedToday.toLocaleString()} exported today`
        : lastExportAt
          ? `Last export ${exportTimeLabel(lastExportAt)}`
          : 'Nothing exported yet',
    };
  if (unsaved) return { kind: 'unsaved', text: 'Changes not saved' };
  if (exportedToday)
    return { kind: 'ready', text: `Ready · ${exportedToday.toLocaleString()} exported today` };
  return {
    kind: 'ready',
    text: `Ready · last export ${lastExportAt ? exportTimeLabel(lastExportAt) : '—'}`,
  };
}

/** "2:14 PM" today, "Sep 24" before that. */
export function exportTimeLabel(iso: string, now = new Date()) {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return at.toDateString() === now.toDateString()
    ? at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** The day filter for Activity: any date, then today and the six days before it (local yyyy-mm-dd). */
export function exportHistoryDayChoices(now = new Date()): Array<{ value: string; label: string }> {
  const pad = (value: number) => String(value).padStart(2, '0');
  const days = Array.from({ length: 7 }, (_, index) => {
    const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() - index);
    const value = `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
    const label =
      index === 0
        ? 'Today'
        : index === 1
          ? 'Yesterday'
          : day.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    return { value, label };
  });
  return [{ value: '', label: 'Any date' }, ...days];
}

/** The newest delivered export among these rows, for "last export". */
export function latestExportAt(
  documents: ReadonlyArray<{ state: string; verifiedAtUtc?: string | null; frozenAtUtc: string }>,
): string | null {
  let latest: string | null = null;
  for (const document of documents) {
    if (document.state !== 'exported' && document.state !== 'purged') continue;
    const at = document.verifiedAtUtc ?? document.frozenAtUtc;
    if (at && (!latest || Date.parse(at) > Date.parse(latest))) latest = at;
  }
  return latest;
}
